import Link from 'next/link';
import type { MouseEventHandler, ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import { focusRing } from './focus-ring';
import styles from './nav-pill.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type Orientation = 'horizontal' | 'vertical' | 'tab';

interface NavPillProps {
  active?: boolean;
  /** horizontal (header) · vertical (drawer row) · tab (action bar). */
  orientation?: Orientation;
  icon?: IconName;
  badge?: number | string | null;
  /** When set, renders a Next `Link`; otherwise a `button`. */
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  /** Inside a NavRail: sit above the sliding pill (transparent active bg). */
  sliding?: boolean;
  children: ReactNode;
  className?: string;
  [key: string]: unknown;
}

/**
 * The canonical active-nav treatment — soft-accent pill when active, quiet
 * ghost otherwise. One element for the header (horizontal), drawer row
 * (vertical, leading icon + left accent rule) and action bar (tab, stacked).
 * Ported from the #302 element library (`NavPill`).
 */
export function NavPill({
  active = false,
  orientation = 'horizontal',
  icon,
  badge,
  href,
  onClick,
  sliding = false,
  children,
  className,
  ...rest
}: NavPillProps) {
  const cls = cx(
    styles.navPill,
    styles[orientation],
    active && styles.active,
    sliding && styles.sliding,
    focusRing,
    className,
  );

  const inner = (
    <>
      {icon ? (
        <span className={styles.icon}>
          <Icon name={icon} size={18} strokeWidth={1.75} aria-hidden="true" />
        </span>
      ) : null}
      <span className={styles.label}>{children}</span>
      {badge != null && badge !== 0 ? <span className={styles.badge}>{badge}</span> : null}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className={cls}
        {...rest}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-current={active ? 'page' : undefined} className={cls} {...rest}>
      {inner}
    </button>
  );
}
